<template>
  <div>
    <!-- Top Announcement Bar -->
    <div class="bg-gray-800 text-white py-1.5 text-xs relative overflow-hidden">
      <!-- Loading Progress Bar -->
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-500"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="productStore.loading || cmsStore.loading" class="absolute top-0 left-0 right-0 h-[2px] z-50">
          <div class="h-full bg-yellow-400 animate-loading-bar shadow-[0_0_10px_rgba(250,204,21,0.8)]"></div>
        </div>
      </transition>

      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <span class="font-medium tracking-wide">24/7 Emergency Support: <strong class="text-yellow-400">+974 3081 2223</strong></span>
        <div class="hidden md:flex space-x-6">
          <NuxtLink to="/about" class="text-slate-300 hover:text-yellow-400 transition-colors">About Us</NuxtLink>
          <NuxtLink to="/contact" class="text-slate-300 hover:text-yellow-400 transition-colors">Contact</NuxtLink>
          <NuxtLink to="/track-order" class="text-slate-300 hover:text-yellow-400 transition-colors">Track Order</NuxtLink>
        </div>
      </div>
    </div>

    <!-- Main Navigation (Standard E-commerce) -->
    <header class="bg-red-600 text-white sticky top-0 z-[100] shadow-lg">
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
        <div class="flex items-center justify-between gap-2 md:gap-8 flex-nowrap">
          
          <!-- Logo -->
          <NuxtLink to="/" class="flex items-center shrink-0 order-1" aria-label="Home">
            <NuxtImg src="/images/logo.png" alt="Alarm Supplies" class="h-6 md:h-10 w-auto brightness-0 invert" />
          </NuxtLink>
          
          <!-- Permanent Search Bar -->
          <div class="order-2 flex-1 mx-2 md:mx-0 md:px-8 mt-0 relative min-w-0">
            <div class="flex w-full max-w-4xl mx-auto rounded-xl overflow-hidden shadow-sm border border-white/20 relative z-[110]">
              <select v-model="navSearchCategory" aria-label="Search Category" class="hidden md:block bg-slate-100 text-slate-800 border-0 px-4 py-2.5 text-xs outline-none w-40 font-bold cursor-pointer">
                <option>All Categories</option>
                <option v-for="cat in productStore.categories" :key="cat" class="text-slate-900 bg-white">{{ cat }}</option>
              </select>
              <div class="hidden md:block w-px bg-slate-300 self-stretch"></div>
              <input 
                v-model="navSearchQuery"
                type="text" 
                aria-label="Search Query"
                placeholder="Search..." 
                class="flex-1 px-2 md:px-4 py-2 md:py-2.5 text-slate-900 bg-white border-0 focus:outline-none text-xs md:text-sm font-medium placeholder:text-slate-400 relative z-10 min-w-0 w-full" 
                @keyup.enter="handleNavSearch"
                @focus="isSearchFocused = true"
                @blur="handleSearchBlur"
                ref="searchInput"
              />
              <button @click="handleNavSearch" aria-label="Submit Search" class="bg-[#febd69] hover:bg-[#f3a847] active:bg-[#e8a020] text-slate-900 px-3 md:px-6 py-2 md:py-2.5 flex items-center justify-center font-bold transition-all shrink-0 cursor-pointer group/search relative z-10">
                <component :is="SearchIcon" class="w-4 h-4 group-hover/search:scale-110 transition-transform" />
              </button>
            </div>

            <!-- Search Suggestions Dropdown -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-2"
            >
              <div v-if="isSearchFocused && (searchHistory.length > 0 || searchSuggestions.length > 0 || navSearchQuery)" class="absolute top-full mt-2 w-full md:w-[calc(100%-4rem)] max-w-4xl left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-slate-100 z-[120] overflow-hidden text-slate-900">
                <!-- Search Suggestions -->
                <div v-if="navSearchQuery && searchSuggestions.length > 0" class="py-2">
                  <div class="px-4 py-2 bg-slate-50 border-b border-slate-100">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400">Products</span>
                  </div>
                  <NuxtLink 
                    v-for="product in searchSuggestions" 
                    :key="product.id"
                    :to="`/product/${product.slug}`"
                    @mousedown.prevent="navigateTo(`/product/${product.slug}`); isSearchFocused = false"
                    class="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors cursor-pointer group/sugg"
                  >
                    <div class="w-8 h-8 rounded bg-white border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                      <NuxtImg v-if="product.image" :src="product.image" :alt="product.name" class="w-full h-full object-contain" width="32" height="32" />
                      <component v-else :is="PackageIcon" class="w-4 h-4 text-slate-300" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-medium text-slate-700 group-hover/sugg:text-red-600 truncate">{{ product.name }}</div>
                      <div class="text-[10px] text-slate-400">{{ product.category }}</div>
                    </div>
                  </NuxtLink>
                </div>
                
                <!-- No Results -->
                <div v-else-if="navSearchQuery && searchSuggestions.length === 0" class="py-6 px-4 text-center">
                  <p class="text-sm text-slate-500">No results found for "<span class="font-bold">{{ navSearchQuery }}</span>"</p>
                </div>
                
                <!-- Recent Searches -->
                <div v-if="filteredSearchHistory.length > 0" class="py-2">
                  <div class="px-4 py-2 flex items-center justify-between bg-slate-50 border-b border-slate-100">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400">Recent Searches</span>
                    <button @mousedown.prevent="clearHistory" class="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-widest transition-colors cursor-pointer">Clear</button>
                  </div>
                  <button 
                    v-for="query in filteredSearchHistory" 
                    :key="query"
                    @mousedown.prevent="applyHistorySearch(query)"
                    class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer group/hist"
                  >
                    <div class="flex items-center gap-3 text-slate-600 group-hover/hist:text-slate-900">
                      <component :is="ClockIcon" class="w-4 h-4 text-slate-300 group-hover/hist:text-red-500 transition-colors" />
                      <span class="text-sm">{{ query }}</span>
                    </div>
                    <button @mousedown.prevent.stop="removeFromHistory(query)" class="text-slate-300 hover:text-red-500 p-1 opacity-0 group-hover/hist:opacity-100 transition-all cursor-pointer">
                      <component :is="XIcon" class="w-3 h-3" />
                    </button>
                  </button>
                </div>
              </div>
            </transition>
          </div>
          
          <!-- Right Icons -->
          <div class="flex items-center space-x-2 md:space-x-5 order-3 shrink-0 ml-auto justify-end">

            <!-- Account & Quick Access -->
            <ClientOnly>
              <template #fallback>
                <div class="flex items-center gap-2 text-white animate-pulse">
                  <div class="w-9 h-9 rounded-full bg-red-700/50 flex items-center justify-center">
                    <component :is="UserIcon" class="w-6 h-6 opacity-30" />
                  </div>
                  <div class="hidden lg:block leading-tight">
                    <span class="block w-16 h-2 bg-red-700/50 rounded mb-1"></span>
                    <span class="block w-20 h-4 bg-red-700/50 rounded"></span>
                  </div>
                </div>
              </template>
              
              <!-- Account Link (Not Logged In) -->
              <NuxtLink v-if="!authStore.isAuthenticated" to="/auth/login" class="flex items-center gap-2 text-white group" aria-label="Sign In">
                <div class="w-9 h-9 rounded-full flex items-center justify-center group-hover:bg-red-700 transition-colors">
                  <component :is="UserIcon" class="w-6 h-6" />
                </div>
                <div class="hidden lg:block leading-tight">
                  <span class="block text-[10px] text-red-100 uppercase tracking-wider">Hello, Sign In</span>
                  <span class="block text-sm font-medium text-white">Account</span>
                </div>
              </NuxtLink>

              <!-- My Account (Logged In) -->
              <div v-else class="relative group">
                <button class="flex items-center gap-2 text-white group cursor-pointer h-full" aria-label="My Account">
                  <div class="w-9 h-9 rounded-full bg-red-700 flex items-center justify-center group-hover:bg-red-800 transition-colors shadow-inner shadow-black/10">
                    <component :is="UserIcon" class="w-6 h-6" />
                  </div>
                  <div class="hidden lg:block leading-tight text-left">
                    <span class="block text-[10px] text-red-100 uppercase tracking-wider">Hello, {{ authStore.user?.displayName || 'User' }}</span>
                    <span class="block text-sm font-medium text-white">My Account</span>
                  </div>
                </button>
                
                <!-- Dropdown Menu -->
                <div class="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl py-3 invisible group-hover:visible opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all z-50 border border-slate-100 ring-1 ring-slate-200/50">
                  <div class="px-4 py-2 border-b border-slate-50 mb-2">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Account Management</p>
                  </div>
                  <NuxtLink to="/user/profile" class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 font-medium group/item">
                      <component :is="UserIcon" class="w-4 h-4 text-slate-400 group-hover/item:text-red-600" />
                      My Profile
                  </NuxtLink>
                  <NuxtLink to="/user/orders" class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 font-medium group/item">
                      <component :is="PackageIcon" class="w-4 h-4 text-slate-400 group-hover/item:text-red-600" />
                      Order History
                  </NuxtLink>
                  <div v-if="authStore.isAdmin" class="mt-2 pt-2 border-t border-slate-50">
                      <NuxtLink to="/admin" class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 font-medium group/item">
                          <component :is="FlameIcon" class="w-4 h-4 text-red-600" />
                          Admin Panel
                      </NuxtLink>
                  </div>
                  <div class="mt-2 pt-2 border-t border-slate-50">
                      <button @click="authStore.logout()" class="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-bold cursor-pointer group/item" aria-label="Log Out">
                          <component :is="LogOutIcon" class="w-4 h-4" />
                          Log Out
                      </button>
                  </div>
                </div>
              </div>
            </ClientOnly>

            <NuxtLink to="/cart" class="flex items-center gap-2 text-white group relative" aria-label="Shopping Cart">
              <div class="relative">
                <component :is="ShoppingCartIcon" class="w-7 h-7 group-hover:scale-110 transition-transform" />
                <ClientOnly>
                  <span v-if="cartStore.totalItems > 0" class="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-yellow-400 text-slate-900 text-[11px] font-bold flex items-center justify-center shadow">
                    {{ cartStore.totalItems }}
                  </span>
                </ClientOnly>
              </div>
              <div class="hidden lg:block leading-tight">
                <span class="block text-[10px] text-red-100 uppercase tracking-wider">My</span>
                <span class="block text-sm font-medium text-white">Cart</span>
              </div>
            </NuxtLink>

          </div>
        </div>
      </div>
      
      <!-- Horizontal Categories Bar -->
      <div class="bg-gray-800 text-white">
        <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex items-center h-12">
          <!-- Mobile Scroll Indicator -->
          <span class="md:hidden absolute -bottom-1.5 right-4 z-50 bg-red-600 text-white text-[8px] px-2 py-0.5 rounded-full animate-blink font-bold shadow-lg border border-red-500 flex items-center gap-1 pointer-events-none">
            DRAG <span class="text-[8px] tracking-widest">&larr;&rarr;</span>
          </span>
            <!-- All Departments Dropdown -->
            <div ref="categoryDropdownRef" class="relative h-full shrink-0 group/dept">
                <button 
                  @click="isCategoryMenuOpen = !isCategoryMenuOpen; isBrandsMenuOpen = false"
                  class="flex items-center justify-center hover:bg-white/10 px-4 md:px-5 h-full transition-all border-r border-white/10 font-bold text-yellow-400 cursor-pointer active:bg-white/20 select-none"
                  aria-label="All Departments"
                  title="All Departments"
                >
                  <component :is="MenuIcon" class="w-5 h-5" /> 
                  <component :is="ChevronDownIcon" :class="['w-3 h-3 transition-transform duration-300 ml-1', isCategoryMenuOpen ? 'rotate-180' : '']" />
                </button>

                <!-- Dropdown Menu -->
                <transition
                    enter-active-class="transition duration-200 ease-out"
                    enter-from-class="transform -translate-y-4 opacity-0"
                    enter-to-class="transform translate-y-0 opacity-100"
                    leave-active-class="transition duration-150 ease-in"
                    leave-from-class="transform translate-y-0 opacity-100"
                    leave-to-class="transform -translate-y-4 opacity-0"
                >
                    <div v-if="isCategoryMenuOpen" class="absolute left-0 top-full w-[280px] sm:w-[400px] md:w-[700px] max-w-[95vw] bg-white rounded-b-2xl md:rounded-br-[2rem] shadow-2xl py-6 z-[110] border border-slate-100 overflow-hidden">
                        <div class="px-6 mb-4">
                            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Safety Categories</p>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-2 px-6">
                            <NuxtLink 
                                v-for="cat in productStore.categories" 
                                :key="cat" 
                                :to="`/category/${slugify(cat)}`"
                                @click="isCategoryMenuOpen = false"
                                class="flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all group/item"
                            >
                                <div class="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-hover/item:bg-white group-hover/item:text-red-600 shadow-sm transition-colors shrink-0">
                                    <component :is="getCategoryIcon(cat)" class="w-4 h-4" />
                                </div>
                                <span class="font-bold text-[11px] uppercase tracking-wider">{{ cat }}</span>
                            </NuxtLink>
                        </div>
                        <div class="mt-6 pt-6 border-t border-slate-50 px-6">
                            <NuxtLink to="/shop" @click="isCategoryMenuOpen = false" class="flex items-center justify-center w-full py-4 rounded-2xl bg-slate-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg shadow-slate-900/10 hover:shadow-red-600/20">
                                View Full Inventory
                            </NuxtLink>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Brands Dropdown -->
            <div ref="brandsDropdownRef" class="relative h-full shrink-0 group/brands">
                <button 
                  @click="isBrandsMenuOpen = !isBrandsMenuOpen; isCategoryMenuOpen = false"
                  class="flex items-center justify-center hover:bg-white/10 px-4 md:px-5 h-full transition-all border-r border-white/10 font-bold text-slate-200 cursor-pointer active:bg-white/20 select-none"
                  aria-label="Manufacturers"
                  title="Manufacturers"
                >
                  <span class="mr-2 text-[10px] md:text-xs tracking-wider uppercase">Brands</span>
                  <component :is="ChevronDownIcon" :class="['w-3 h-3 transition-transform duration-300', isBrandsMenuOpen ? 'rotate-180' : '']" />
                </button>

                <!-- Dropdown Menu -->
                <transition
                    enter-active-class="transition duration-200 ease-out"
                    enter-from-class="transform -translate-y-4 opacity-0"
                    enter-to-class="transform translate-y-0 opacity-100"
                    leave-active-class="transition duration-150 ease-in"
                    leave-from-class="transform translate-y-0 opacity-100"
                    leave-to-class="transform -translate-y-4 opacity-0"
                >
                    <div v-if="isBrandsMenuOpen" class="absolute left-0 md:-left-20 top-full w-[280px] sm:w-[400px] md:w-[700px] max-w-[95vw] bg-white rounded-b-2xl md:rounded-br-[2rem] shadow-2xl py-6 z-[110] border border-slate-100 overflow-hidden">
                        <div class="px-6 mb-4">
                            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Manufacturers</p>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-2 px-6">
                            <NuxtLink 
                                v-for="brand in productStore.brands" 
                                :key="brand.id" 
                                :to="`/shop?brand=${encodeURIComponent(brand.name)}`"
                                @click="isBrandsMenuOpen = false"
                                class="flex items-center gap-3 px-3 py-2 text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all group/item"
                            >
                                <div class="w-8 h-8 flex items-center justify-center text-slate-400 group-hover/item:text-red-600 transition-colors shrink-0 overflow-hidden">
                                    <NuxtImg v-if="brand.logo" :src="brand.logo" :alt="brand.name" class="w-full h-full object-contain" width="32" height="32" />
                                    <component v-else :is="PackageIcon" class="w-5 h-5" />
                                </div>
                                <span class="font-bold text-[11px] uppercase tracking-wider">{{ brand.name }}</span>
                            </NuxtLink>
                        </div>
                        <div class="mt-6 pt-6 border-t border-slate-50 px-6">
                            <NuxtLink to="/shop" @click="isBrandsMenuOpen = false" class="flex items-center justify-center w-full py-4 rounded-2xl bg-slate-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg shadow-slate-900/10 hover:shadow-red-600/20">
                                View Full Inventory
                            </NuxtLink>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Horizontal Quick Links -->
            <div class="flex-1 min-w-0 relative h-full flex items-center group/nav">
              <!-- Left Scroll Button -->
              <button 
                @click="scrollLinks('left')"
                class="absolute left-0 z-10 h-full px-2 bg-gradient-to-r from-gray-800 via-gray-800 to-transparent text-white opacity-0 group-hover/nav:opacity-100 transition-opacity disabled:opacity-0 hidden md:flex items-center justify-center cursor-pointer"
                aria-label="Scroll Left"
                title="Scroll Left"
              >
                <component :is="ChevronLeftIcon" class="w-4 h-4" />
              </button>

              <!-- Horizontal Quick Links Scroll Area -->
              <div 
                ref="quickLinksRef"
                @wheel="handleWheelScroll"
                @mousedown="handleMouseDown"
                class="w-full flex h-full items-center overflow-x-auto scrollbar-hide whitespace-nowrap select-none cursor-grab active:cursor-grabbing px-4"
              >
                <NuxtLink 
                  v-for="cat in productStore.categories.slice(0, 8)" 
                  :key="cat" 
                  :to="`/category/${slugify(cat)}`" 
                  @click="handleLinkClick"
                  @dragstart.prevent
                  class="hover:bg-white/10 px-3 md:px-4 h-full flex items-center transition-colors shrink-0 text-slate-200 hover:text-white text-[10px] md:text-xs font-bold uppercase tracking-tight"
                >
                  {{ cat }}
                </NuxtLink>
              </div>

              <!-- Right Scroll Button -->
              <button 
                @click="scrollLinks('right')"
                class="absolute right-0 z-10 h-full px-2 bg-gradient-to-l from-gray-800 via-gray-800 to-transparent text-white opacity-0 group-hover/nav:opacity-100 transition-opacity disabled:opacity-0 hidden md:flex items-center justify-center cursor-pointer"
                aria-label="Scroll Right"
                title="Scroll Right"
              >
                <component :is="ChevronRightIcon" class="w-4 h-4" />
              </button>
            </div>
        </div>
      </div>
    </header>

    <main class="flex-1 bg-gray-50 min-h-screen">
      <slot />
    </main>

    <footer class="bg-gray-900 text-slate-400">
      <div class="border-b border-gray-700">
        <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div class="lg:col-span-2">
              <NuxtLink to="/" class="flex items-center mb-6" aria-label="Home">
                <NuxtImg src="/images/logo.png" alt="Alarm Supplies" class="h-8 md:h-10 w-auto brightness-0 invert" />
              </NuxtLink>
              <p class="text-sm text-slate-400 mb-6 max-w-sm leading-relaxed">
                {{ cmsStore.home.footer.aboutText }}
              </p>
              <div class="flex space-x-3">
                <a :href="cmsStore.home.footer.socials.facebook" aria-label="Facebook" class="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"><component :is="FacebookIcon" class="w-4 h-4" /></a>
                <a :href="cmsStore.home.footer.socials.twitter" aria-label="Twitter" class="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"><component :is="TwitterIcon" class="w-4 h-4" /></a>
                <a :href="cmsStore.home.footer.socials.linkedin" aria-label="LinkedIn" class="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"><component :is="LinkedinIcon" class="w-4 h-4" /></a>
              </div>
            </div>
            
            <div>
              <h3 class="text-white font-bold mb-5 uppercase tracking-wider text-xs">Products</h3>
              <ul class="space-y-3 text-sm">
                <li><NuxtLink to="/category/fire-extinguishers" class="text-slate-400 hover:text-white transition-colors">Fire Extinguishers</NuxtLink></li>
                <li><NuxtLink to="/category/smoke-detectors" class="text-slate-400 hover:text-white transition-colors">Smoke Detectors</NuxtLink></li>
                <li><NuxtLink to="/category/fire-alarms" class="text-slate-400 hover:text-white transition-colors">Alarm Systems</NuxtLink></li>
              </ul>
            </div>
            
            <div>
              <h3 class="text-white font-bold mb-5 uppercase tracking-wider text-xs">Support</h3>
              <ul class="space-y-3 text-sm">
                <li><NuxtLink to="/contact" class="text-slate-400 hover:text-white transition-colors">Contact Us</NuxtLink></li>
                <li><NuxtLink to="/faq" class="text-slate-400 hover:text-white transition-colors">Help Center</NuxtLink></li>
                <li><NuxtLink to="/returns" class="text-slate-400 hover:text-white transition-colors">Returns & Refunds</NuxtLink></li>
                <li><NuxtLink to="/warranty" class="text-slate-400 hover:text-white transition-colors">Warranty Info</NuxtLink></li>
                <li><button @click="showCallbackModal = true" class="text-slate-400 hover:text-white transition-colors cursor-pointer" aria-label="Request Callback">Request Callback</button></li>
              </ul>
            </div>
            
            <div>
              <h3 class="text-white font-bold mb-5 uppercase tracking-wider text-xs">Contact</h3>
              <ul class="space-y-4 text-sm">
                <li class="flex items-start gap-3">
                  <component :is="MapPinIcon" class="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  <span>{{ cmsStore.home.footer.address }}</span>
                </li>
                <li class="flex items-center gap-3">
                  <component :is="PhoneIcon" class="w-4 h-4 text-slate-500 shrink-0" />
                  <div class="flex flex-col">
                    <span>{{ cmsStore.home.footer.phone }}</span>
                    <span class="text-[10px] text-slate-500">Mob: {{ cmsStore.home.footer.mobile }}</span>
                  </div>
                </li>
                <li class="flex items-center gap-3">
                  <component :is="MailIcon" class="w-4 h-4 text-slate-500 shrink-0" />
                  <span>{{ cmsStore.home.footer.email }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs flex flex-col md:flex-row justify-between items-center text-slate-600">
        <p>&copy; {{ new Date().getFullYear() }} ALARM Fire Safety Equipment. All rights reserved.</p>
        <div class="flex space-x-4 mt-3 md:mt-0">
          <NuxtImg src="/images/payments/visa.svg" alt="Visa" class="h-5 opacity-40 grayscale hover:opacity-70 hover:grayscale-0 transition-all" width="60" height="20" />
          <NuxtImg src="/images/payments/mastercard.svg" alt="Mastercard" class="h-5 opacity-40 grayscale hover:opacity-70 hover:grayscale-0 transition-all" width="34" height="20" />
        </div>
      </div>
    </footer>
    <!-- Global Lead Components -->
    <LazyLeadsWhatsAppFloating />
    <LazyLeadsCookieConsent />
    <LazyLeadsNewsletterPopup />
    <LazyLeadsCallbackModal v-model="showCallbackModal" />

    <!-- Auth Popup for exploring users -->
    <LazyAuthPopup />


  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from '#app'
import { onClickOutside } from '@vueuse/core'
import { slugify } from '~/utils/slugify'
import { 
  Search as SearchIcon, 
  Clock as ClockIcon,
  X as XIcon,
  ShoppingCart as ShoppingCartIcon, 
  User as UserIcon,
  Flame as FlameIcon,
  Menu as MenuIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Linkedin as LinkedinIcon,
  Package as PackageIcon,
  LogOut as LogOutIcon,
  MapPin as MapPinIcon,
  Phone as PhoneIcon,
  Mail as MailIcon,
  ChevronDown as ChevronDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Shield as ShieldIcon,
  Zap as ZapIcon,
  Wind as WindIcon,
  Layers as LayersIcon,
  HeartPulse as HeartPulseIcon,
  Lightbulb as LightbulbIcon,
  Droplets as DropletsIcon,
  MessageCircle as MessageCircleIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  AlertTriangle as AlertIcon,
  Check as CheckIcon,
  UploadCloud as UploadIcon
} from 'lucide-vue-next'
import { useCartStore } from '~/stores/cart'
import { useProductStore, categoryIcons } from '~/stores/products'
import { useAuthStore } from '~/stores/auth'
import { useTrackingStore } from '~/stores/tracking'
import { useCMSStore } from '~/stores/cms'

const router = useRouter()
const route = useRoute()
const navSearchQuery = ref('')
const navSearchCategory = ref('All Categories')
const isSearchFocused = ref(false)
const searchHistory = ref<string[]>([])
const showCallbackModal = ref(false)

const saveHistory = () => {
  if (process.client) {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory.value))
  }
}

const addToHistory = (query: string) => {
  if (process.client) {
    if (navSearchQuery.value && !searchHistory.value.includes(navSearchQuery.value)) {
      searchHistory.value.unshift(navSearchQuery.value)
      if (searchHistory.value.length > 5) searchHistory.value.pop()
      localStorage.setItem('search_history', JSON.stringify(searchHistory.value))
      
      const trackingStore = useTrackingStore()
      trackingStore.addSearchKeyword(navSearchQuery.value)
      trackingStore.addScore(2, 'Searched for: ' + navSearchQuery.value)
    }
  }
  searchHistory.value = searchHistory.value.filter(q => q !== query)
  saveHistory()
}

const removeFromHistory = (query: string) => {
  searchHistory.value = searchHistory.value.filter(q => q !== query)
  saveHistory()
}

const clearHistory = () => {
  searchHistory.value = []
  saveHistory()
}

const handleSearchBlur = () => {
  setTimeout(() => {
    isSearchFocused.value = false
  }, 200)
}

const applyHistorySearch = (query: string) => {
  navSearchQuery.value = query
  handleNavSearch()
}

const searchSuggestions = computed(() => {
  if (!navSearchQuery.value.trim()) return []
  const query = navSearchQuery.value.toLowerCase()
  return productStore.products.filter((p: any) => 
    p.name.toLowerCase().includes(query) || 
    p.category.toLowerCase().includes(query)
  ).slice(0, 5)
})

const filteredSearchHistory = computed(() => {
  if (!navSearchQuery.value.trim()) {
    return searchHistory.value.slice(0, 3)
  }
  const q = navSearchQuery.value.toLowerCase()
  return searchHistory.value.filter(h => h.toLowerCase().includes(q)).slice(0, 3)
})

const handleNavSearch = () => {
  const query: any = {}
  
  if (navSearchQuery.value.trim()) {
    query.search = navSearchQuery.value.trim()
    addToHistory(query.search)
  }
  
  if (navSearchCategory.value !== 'All Categories') {
    query.category = navSearchCategory.value
  }

  router.push({
    path: '/shop',
    query
  })
  
  // Only clear the text, keep the category if selected
  if (navSearchQuery.value.trim()) {
    navSearchQuery.value = ''
  }
  isSearchFocused.value = false
}

const cartStore = useCartStore()
const productStore = useProductStore()
const authStore = useAuthStore()
const cmsStore = useCMSStore()

// Category Menu State
const isCategoryMenuOpen = ref(false)
const isBrandsMenuOpen = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)

const categoryDropdownRef = ref<HTMLElement | null>(null)
const brandsDropdownRef = ref<HTMLElement | null>(null)

// Close dropdowns when clicking outside
onClickOutside(categoryDropdownRef, () => {
  isCategoryMenuOpen.value = false
})

onClickOutside(brandsDropdownRef, () => {
  isBrandsMenuOpen.value = false
})

// Close dropdowns on route changes (e.g. when choosing category or brand)
watch(() => route.fullPath, () => {
  isCategoryMenuOpen.value = false
  isBrandsMenuOpen.value = false
})

const handleWheelScroll = (e: WheelEvent) => {
  const container = e.currentTarget as HTMLElement
  if (container) {
    container.scrollLeft += e.deltaY
  }
}

const quickLinksRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)

// Mouse dragging scroll implementation
let isDown = false
let startX = 0
let scrollLeft = 0

const handleMouseDown = (e: MouseEvent) => {
  const container = quickLinksRef.value
  if (!container) return
  isDown = true
  isDragging.value = false
  startX = e.pageX - container.offsetLeft
  scrollLeft = container.scrollLeft
  
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)
}

const handleMouseMove = (e: MouseEvent) => {
  if (!isDown) return
  const container = quickLinksRef.value
  if (!container) return
  const x = e.pageX - container.offsetLeft
  const walk = (x - startX) * 1.5 // Scroll multiplier
  if (Math.abs(walk) > 5) {
    isDragging.value = true
  }
  e.preventDefault()
  container.scrollLeft = scrollLeft - walk
}

const handleMouseUp = () => {
  isDown = false
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
  if (isDragging.value) {
    setTimeout(() => {
      isDragging.value = false
    }, 50)
  }
}

const handleLinkClick = (e: MouseEvent) => {
  if (isDragging.value) {
    e.preventDefault()
    e.stopPropagation()
  }
}

const scrollLinks = (direction: 'left' | 'right') => {
  if (quickLinksRef.value) {
    const scrollAmount = 300;
    const targetScroll = quickLinksRef.value.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
    quickLinksRef.value.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }
}

const getCategoryIcon = (categoryName: string) => {
    const iconName = categoryIcons[categoryName]
    switch (iconName) {
        case 'Flame': return FlameIcon
        case 'Shield': return ShieldIcon
        case 'Zap': return ZapIcon
        case 'Wind': return WindIcon
        case 'Layers': return LayersIcon
        case 'HeartPulse': return HeartPulseIcon
        case 'Lightbulb': return LightbulbIcon
        case 'Droplets': return DropletsIcon
        case 'Settings': return SettingsIcon
        case 'Info': return InfoIcon
        case 'Alert': return AlertIcon
        case 'Check': return CheckIcon
        case 'Upload': return UploadIcon
        case 'Package': return PackageIcon
        default: return PackageIcon
    }
}

onMounted(async () => {
  if (process.client) {
    cartStore.initCart()
    await cmsStore.fetchHomeCMS()
    
    // Load search history
    const storedHistory = localStorage.getItem('searchHistory')
    if (storedHistory) {
      try {
        searchHistory.value = JSON.parse(storedHistory)
      } catch (e) {}
    }
  }
})

onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('mouseup', handleMouseUp)
  }
})

// Correct server-side products fetching to prevent hydration mismatch
if (productStore.products.length === 0) {
  productStore.fetchProducts()
}

// Phase 5: Local SEO & Entity Mapping (Qatar)
useSchemaOrg([
  defineLocalBusiness({
    name: 'Security Solutions Qatar',
    address: {
      addressCountry: 'QA',
      addressLocality: 'Doha',
      streetAddress: cmsStore.home?.footer?.address || 'Salwa Road',
    },
    telephone: cmsStore.home?.footer?.phone || '+974 3081 2223',
    email: cmsStore.home?.footer?.email || 'sales@alarmsupplies.qa',
    image: 'https://alarmsupplies.qa/images/logo.png',
    priceRange: '$$',
    openingHoursSpecification: [
      {
        dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        opens: '08:00',
        closes: '18:00'
      }
    ],
    areaServed: {
      '@type': 'Country',
      name: 'Qatar'
    }
  }),
  // Phase 16: Google Sitelinks Search Box Schema
  defineWebSite({
    name: 'Alarm Supplies Qatar',
    url: 'https://alarmsupplies.qa',
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: 'https://alarmsupplies.qa/shop?search={search_term_string}',
        'query-input': 'required name=search_term_string'
      }
    ]
  })
])
</script>

<style scoped>
@keyframes loading-bar {
  0% { transform: translateX(-100%); }
  50% { transform: translateX(0); }
  100% { transform: translateX(100%); }
}
.animate-loading-bar {
  animation: loading-bar 2s ease-in-out infinite;
  width: 100%;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.animate-blink {
  animation: blink 1s ease-in-out infinite;
}
</style>
